import type { LayoutField } from "@/components/features/matter/file-repo/signature-requests/SignatureRequestPdfLayoutEditor.constants";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DateFormField,
  Form,
  Input,
  Typography,
} from "@/components/ui";
import { CalendarDays, CheckSquare2, Type } from "lucide-react";
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useForm } from "react-hook-form";

import { ExtendSignaturePadDialog } from "./ExtendSignaturePadDialog";
import { updateReusableSignerFieldValues } from "./reusableSignerFieldValue";

export type DemoSignedField = LayoutField & {
  imageDataUrl?: string;
  checked?: boolean;
};

type DemoFieldValues = Record<string, string>;

type DemoSignatureFieldEditorDialogProps = {
  activeFieldId: string | null;
  fieldValues: DemoFieldValues;
  fields: DemoSignedField[];
  onClose: () => void;
  setFieldValues: Dispatch<SetStateAction<DemoFieldValues>>;
  setFields: Dispatch<SetStateAction<DemoSignedField[]>>;
};

function isSignatureField(field: LayoutField) {
  return field.type === "signature" || field.type === "initials";
}

function isCheckboxField(field: LayoutField) {
  return field.type === "checkbox";
}

export function DemoSignatureFieldEditorDialog({
  activeFieldId,
  fieldValues,
  fields,
  onClose,
  setFieldValues,
  setFields,
}: DemoSignatureFieldEditorDialogProps) {
  const [draftValue, setDraftValue] = useState("");
  const [signaturePadFieldId, setSignaturePadFieldId] = useState("");
  const dateForm = useForm<{ date: string }>({
    defaultValues: { date: "" },
    mode: "onSubmit",
  });
  const activeField =
    fields.find((field) => field.id === activeFieldId) ?? null;

  useEffect(() => {
    if (!activeField) return;

    if (isSignatureField(activeField)) {
      setDraftValue("");
      setSignaturePadFieldId(activeField.id);
      return;
    }

    setSignaturePadFieldId("");

    if (activeField.type === "date") {
      dateForm.reset({
        date: fieldValues[activeField.id] ?? "",
      });
      setDraftValue("");
      return;
    }

    setDraftValue(fieldValues[activeField.id] ?? "");
  }, [activeField, dateForm, fieldValues]);

  if (!activeField) {
    return null;
  }

  return (
    <>
      {!isSignatureField(activeField) && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              onClose();
            }
          }}
        >
          <DialogContent className="max-h-[85vh] w-[min(92vw,640px)] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {isCheckboxField(activeField) ? (
                  <CheckSquare2 className="h-4 w-4 text-emerald-600" />
                ) : activeField.type === "date" ? (
                  <CalendarDays className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Type className="h-4 w-4 text-emerald-600" />
                )}
                <span>{activeField.label}</span>
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {activeField.type === "date" ? (
                <Form {...dateForm}>
                  <form className="space-y-2">
                    <DateFormField
                      label={activeField.label}
                      register={dateForm.register("date")}
                      name="date"
                    />
                  </form>
                </Form>
              ) : (
                <div className="space-y-2">
                  <Typography className="text-sm font-medium">Value</Typography>
                  <Input
                    autoFocus
                    type="text"
                    value={draftValue}
                    onChange={(event) => {
                      setDraftValue(event.target.value);
                    }}
                    placeholder={activeField.label}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setFieldValues((current) =>
                      updateReusableSignerFieldValues(
                        fields,
                        current,
                        activeField,
                        activeField.type === "date"
                          ? dateForm.getValues("date")
                          : draftValue,
                      ),
                    );
                    onClose();
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {signaturePadFieldId && (
        <ExtendSignaturePadDialog
          field={{
            bbox: {
              x: 0,
              y: 0,
              width: Math.max(
                (fields.find((field) => field.id === signaturePadFieldId)
                  ?.width ?? 0.2) * 1000,
                1,
              ),
              height: Math.max(
                (fields.find((field) => field.id === signaturePadFieldId)
                  ?.height ?? 0.05) * 1000,
                1,
              ),
            },
            imageDataUrl: fields.find(
              (field) => field.id === signaturePadFieldId,
            )?.imageDataUrl,
          }}
          onCancel={() => {
            setSignaturePadFieldId("");
            onClose();
          }}
          onConfirm={(value) => {
            setFields((current) => {
              return current.map((item) =>
                item.id === signaturePadFieldId
                  ? { ...item, imageDataUrl: value }
                  : item,
              );
            });
            setSignaturePadFieldId("");
            onClose();
          }}
        />
      )}
    </>
  );
}
